/**
 * DocumentDistributionPanel
 * Handles bulk and individual document distribution to members and external recipients
 * Uses useReducer for complex state management
 */

import { useEffect, useReducer, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Mail, CheckCircle2, AlertCircle, Users } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import type {
  GeneratedDocument,
  Recipient,
  DistributionLogEntry
} from "./types/workflow-components.types";

// ============= STATE MANAGEMENT =============

interface DistributionState {
  mode: 'bulk' | 'individual';
  selectedDocuments: Set<string>;
  selectedRecipients: Set<string>;
  distributionLog: DistributionLogEntry[];
  loading: boolean;
  loadingRecipients: boolean;
  loadingLog: boolean;
  sendingBulk: boolean;
  sendingIndividual: Record<string, boolean>;
}

type DistributionAction =
  | { type: 'SET_MODE'; mode: 'bulk' | 'individual' }
  | { type: 'TOGGLE_DOCUMENT'; documentId: string }
  | { type: 'TOGGLE_RECIPIENT'; recipientId: string }
  | { type: 'SELECT_ALL_DOCUMENTS'; documentIds: string[] }
  | { type: 'SELECT_ALL_RECIPIENTS'; recipientIds: string[] }
  | { type: 'SET_DISTRIBUTION_LOG'; log: DistributionLogEntry[] }
  | { type: 'SET_LOADING'; loading: boolean }
  | { type: 'SET_LOADING_RECIPIENTS'; loading: boolean }
  | { type: 'SET_LOADING_LOG'; loading: boolean }
  | { type: 'SET_SENDING_BULK'; sending: boolean }
  | { type: 'SET_SENDING_INDIVIDUAL'; documentId: string; sending: boolean }
  | { type: 'RESET_SELECTION' };

const initialState: DistributionState = {
  mode: 'bulk',
  selectedDocuments: new Set(),
  selectedRecipients: new Set(),
  distributionLog: [],
  loading: false,
  loadingRecipients: false,
  loadingLog: false,
  sendingBulk: false,
  sendingIndividual: {}
};

function distributionReducer(state: DistributionState, action: DistributionAction): DistributionState {
  switch (action.type) {
    case 'SET_MODE':
      return { ...state, mode: action.mode };
    
    case 'TOGGLE_DOCUMENT': {
      const newSet = new Set(state.selectedDocuments);
      if (newSet.has(action.documentId)) {
        newSet.delete(action.documentId);
      } else {
        newSet.add(action.documentId);
      }
      return { ...state, selectedDocuments: newSet };
    }
    
    case 'TOGGLE_RECIPIENT': {
      const newSet = new Set(state.selectedRecipients);
      if (newSet.has(action.recipientId)) {
        newSet.delete(action.recipientId);
      } else {
        newSet.add(action.recipientId);
      }
      return { ...state, selectedRecipients: newSet };
    }
    
    case 'SELECT_ALL_DOCUMENTS':
      return { ...state, selectedDocuments: new Set(action.documentIds) };
    
    case 'SELECT_ALL_RECIPIENTS':
      return { ...state, selectedRecipients: new Set(action.recipientIds) };
    
    case 'SET_DISTRIBUTION_LOG':
      return { ...state, distributionLog: action.log };
    
    case 'SET_LOADING':
      return { ...state, loading: action.loading };
    
    case 'SET_LOADING_RECIPIENTS':
      return { ...state, loadingRecipients: action.loading };
    
    case 'SET_LOADING_LOG':
      return { ...state, loadingLog: action.loading };
    
    case 'SET_SENDING_BULK':
      return { ...state, sendingBulk: action.sending };
    
    case 'SET_SENDING_INDIVIDUAL':
      return {
        ...state,
        sendingIndividual: {
          ...state.sendingIndividual,
          [action.documentId]: action.sending
        }
      };
    
    case 'RESET_SELECTION':
      return {
        ...state,
        selectedDocuments: new Set(),
        selectedRecipients: new Set()
      };
    
    default:
      return state;
  }
}

// ============= MAIN COMPONENT =============

export interface DocumentDistributionPanelProps {
  taskId: string;
  unaId: string;
  documents: GeneratedDocument[];
  onDistributionComplete?: () => void;
  additionalRecipients?: Recipient[]; // Pre-populate with officer, external contacts, etc.
}

export const DocumentDistributionPanel = ({
  taskId,
  unaId,
  documents,
  onDistributionComplete,
  additionalRecipients = []
}: DocumentDistributionPanelProps) => {
  const { toast } = useToast();
  const [state, dispatch] = useReducer(distributionReducer, initialState);
  const [recipients, setRecipients] = useState<Recipient[]>([]);

  // Load recipients (UNA members)
  useEffect(() => {
    loadRecipients();
  }, [unaId]);

  // Load distribution log
  useEffect(() => {
    if (documents.length > 0) {
      loadDistributionLog();
    }
  }, [documents]);

  const loadRecipients = async () => {
    dispatch({ type: 'SET_LOADING_RECIPIENTS', loading: true });
    try {
      const { data, error } = await supabase
        .from('una_members')
        .select('id, full_name, email, role')
        .eq('una_id', unaId)
        .eq('status', 'active')
        .order('full_name');

      if (error) throw error;

      const formattedRecipients: Recipient[] = (data || []).map(member => ({
        id: member.id,
        name: member.full_name,
        email: member.email,
        type: 'member',
        role: member.role
      }));

      // Merge UNA members with additional recipients (e.g., officer, external contacts)
      const allRecipients = [...formattedRecipients, ...additionalRecipients];
      setRecipients(allRecipients);
    } catch (error) {
      console.error('Error loading recipients:', error);
      toast({
        title: 'Error Loading Recipients',
        description: 'Failed to load member list',
        variant: 'destructive'
      });
    } finally {
      dispatch({ type: 'SET_LOADING_RECIPIENTS', loading: false });
    }
  };

  const loadDistributionLog = async () => {
    dispatch({ type: 'SET_LOADING_LOG', loading: true });
    try {
      const documentIds = documents.map(d => d.id);
      const { data, error } = await supabase
        .from('document_distribution_log')
        .select('*')
        .in('document_id', documentIds)
        .order('sent_at', { ascending: false });

      if (error) throw error;

      dispatch({ type: 'SET_DISTRIBUTION_LOG', log: data || [] });
    } catch (error) {
      console.error('Error loading distribution log:', error);
    } finally {
      dispatch({ type: 'SET_LOADING_LOG', loading: false });
    }
  };

  const handleBulkDistribution = async () => {
    if (state.selectedDocuments.size === 0 || state.selectedRecipients.size === 0) {
      toast({
        title: 'Selection Required',
        description: 'Please select at least one document and one recipient',
        variant: 'destructive'
      });
      return;
    }

    dispatch({ type: 'SET_SENDING_BULK', sending: true });

    try {
      const selectedDocs = documents.filter(d => state.selectedDocuments.has(d.id));
      const selectedRecips = recipients.filter(r => state.selectedRecipients.has(r.id));

      // Call edge function for bulk distribution
      const { data, error } = await supabase.functions.invoke('send-distribution-emails', {
        body: {
          documentIds: selectedDocs.map(d => d.id),
          taskId,
          recipients: selectedRecips,
          senderMessage: 'Meeting materials for your review'
        }
      });

      if (error) throw error;

      toast({
        title: 'Documents Distributed',
        description: `Sent ${selectedDocs.length} document(s) to ${selectedRecips.length} recipient(s)`,
      });

      dispatch({ type: 'RESET_SELECTION' });
      loadDistributionLog();
      onDistributionComplete?.();
    } catch (error: any) {
      console.error('Bulk distribution error:', error);
      toast({
        title: 'Distribution Failed',
        description: error.message || 'Failed to distribute documents',
        variant: 'destructive'
      });
    } finally {
      dispatch({ type: 'SET_SENDING_BULK', sending: false });
    }
  };

  const getDocumentDistributionStatus = (documentId: string) => {
    const logs = state.distributionLog.filter(log => log.document_id === documentId);
    const sent = logs.filter(log => log.status === 'sent' || log.status === 'delivered').length;
    const failed = logs.filter(log => log.status === 'failed').length;
    
    return { sent, failed, total: logs.length };
  };

  // Get unique recipients who have received documents
  const uniqueRecipientsCount = new Set(
    state.distributionLog
      .filter(l => l.status === 'sent' || l.status === 'delivered')
      .map(l => l.recipient_email)
  ).size;

  // Helper to get sent-to names for a document (deduplicated)
  const getSentToNames = (documentId: string) => {
    const names = state.distributionLog
      .filter(log => log.document_id === documentId && (log.status === 'sent' || log.status === 'delivered'))
      .map(log => log.recipient_name);
    return [...new Set(names)];
  };

  // Helper to get received docs count for a recipient
  const getReceivedDocsForRecipient = (email: string) => {
    return state.distributionLog.filter(
      log => log.recipient_email === email && (log.status === 'sent' || log.status === 'delivered')
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mail className="h-5 w-5" />
          Document Distribution
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Distribution Success Banner */}
        {state.distributionLog.length > 0 && !state.loadingLog && (
          <Alert className="border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950">
            <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
            <AlertDescription className="text-green-800 dark:text-green-200">
              <span className="font-medium">
                Documents previously distributed to {uniqueRecipientsCount} recipient(s)
              </span>
            </AlertDescription>
          </Alert>
        )}

        {/* Document Selection */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <Label className="text-sm font-medium">Select Documents</Label>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => dispatch({
                type: 'SELECT_ALL_DOCUMENTS',
                documentIds: documents.map(d => d.id)
              })}
            >
              Select All
            </Button>
          </div>
          {state.loading ? (
            <div className="text-sm text-muted-foreground">Loading documents...</div>
          ) : (
            <div className="space-y-2">
              {documents.map(doc => {
                const status = getDocumentDistributionStatus(doc.id);
                const hasBeenSent = status.sent > 0;
                const sentToRecipients = getSentToNames(doc.id);
                
                return (
                  <div 
                    key={doc.id} 
                    className={`flex items-center justify-between p-3 border rounded ${
                      hasBeenSent 
                        ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950' 
                        : ''
                    }`}
                  >
                    <div className="flex items-center gap-3 min-h-[44px]">
                      <div className="flex items-center justify-center min-h-[44px] min-w-[44px]">
                        <Checkbox
                          checked={state.selectedDocuments.has(doc.id)}
                          onCheckedChange={() => dispatch({ type: 'TOGGLE_DOCUMENT', documentId: doc.id })}
                        />
                      </div>
                      <div>
                        <span className="text-sm">{doc.document_name}</span>
                        {hasBeenSent && (
                          <p className="text-xs text-green-700 dark:text-green-300">
                            ✓ Sent to: {sentToRecipients.join(', ')}
                          </p>
                        )}
                      </div>
                    </div>
                    {hasBeenSent && (
                      <Badge variant="outline" className="text-xs bg-green-100 text-green-700 border-green-300 dark:bg-green-900 dark:text-green-300 dark:border-green-700">
                        <CheckCircle2 className="h-3 w-3 mr-1" />
                        Sent
                      </Badge>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Recipient Selection */}
        <Separator />
        <div>
          <div className="flex items-center justify-between mb-2">
            <Label className="text-sm font-medium">Select Recipients</Label>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => dispatch({
                type: 'SELECT_ALL_RECIPIENTS',
                recipientIds: recipients.map(r => r.id)
              })}
            >
              Select All
            </Button>
          </div>
          {state.loadingRecipients ? (
            <div className="text-sm text-muted-foreground">Loading recipients...</div>
          ) : (
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {recipients.map(recipient => {
                const receivedDocs = getReceivedDocsForRecipient(recipient.email);
                const hasReceivedDocs = receivedDocs.length > 0;
                
                return (
                  <div 
                    key={recipient.id} 
                    className={`flex items-center gap-3 p-2 border rounded min-h-[44px] ${
                      hasReceivedDocs 
                        ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950' 
                        : ''
                    }`}
                  >
                    <div className="flex items-center justify-center min-h-[44px] min-w-[44px]">
                      <Checkbox
                        checked={state.selectedRecipients.has(recipient.id)}
                        onCheckedChange={() => dispatch({ type: 'TOGGLE_RECIPIENT', recipientId: recipient.id })}
                      />
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-medium">{recipient.name}</div>
                      <div className="text-xs text-muted-foreground">{recipient.email}</div>
                      {hasReceivedDocs && (
                        <div className="text-xs text-green-700 dark:text-green-300">
                          ✓ Received {receivedDocs.length} document(s)
                        </div>
                      )}
                    </div>
                    {recipient.role && (
                      <Badge variant="secondary" className="text-xs">
                        {recipient.role}
                      </Badge>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Action Button */}
        <Button
          onClick={handleBulkDistribution}
          disabled={state.sendingBulk || state.selectedDocuments.size === 0 || state.selectedRecipients.size === 0}
          className="w-full"
        >
          {state.sendingBulk ? (
            <>Sending...</>
          ) : (
            <>
              <Mail className="h-4 w-4 mr-2" />
              Send to {state.selectedRecipients.size} Recipient(s)
            </>
          )}
        </Button>

        {/* Distribution Log Summary */}
        {!state.loadingLog && state.distributionLog.length > 0 && (
          <>
            <Separator />
            <div className="text-sm">
              <div className="font-medium mb-2">Recent Distributions</div>
              <div className="space-y-1 text-xs text-muted-foreground">
                {state.distributionLog.slice(0, 5).map(log => (
                  <div key={log.id} className="flex items-center gap-2">
                    {log.status === 'sent' || log.status === 'delivered' ? (
                      <CheckCircle2 className="h-3 w-3 text-green-600" />
                    ) : (
                      <AlertCircle className="h-3 w-3 text-red-600" />
                    )}
                    <span>{log.recipient_name}</span>
                    <span>•</span>
                    <span>{new Date(log.sent_at).toLocaleDateString()}</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};